import React, { useState } from "react";
import { NavLink } from "react-router-dom";

import SidebarItem from "../elements/SidebarItem";
import AddHorseModal from "../AddHorseModal";
import SourAlert from "../elements/SourAlert";
import useSourAlert from "../../hooks/useSourAlert";
import { authenticationService } from "../../api";
import { addHorseAlerts } from "../../api/helpers";
import pedigreeIcon from "../../assets/icons/product-pedigree.svg";
import catalogueIcon from "../../assets/icons/product-catalogue.svg";
import multiReportIcon from "../../assets/icons/product-multi-report.svg";

const primaryNavigation = [
	{ to: "/", icon: "fa fa-home", text: "Dashboard", end: true },
];

const reportNavigation = [
	{
		to: "/new-pedigree",
		icon: "fa fa-plus",
		text: "Create pedigree",
		tourId: "new-pedigree-nav-item",
		featured: true,
	},
	{ to: "/pedigree-analysis", iconImage: pedigreeIcon, text: "Pedigree reports" },
	{ to: "/premium", iconImage: multiReportIcon, text: "Multi-report tool" },
];

const catalogueNavigation = [
	{ to: "/catalogues", iconImage: catalogueIcon, text: "Catalogues" },
	{ to: "/my-lists", icon: "fa fa-list", text: "My lists" },
	{ icon: "fa fa-horse", text: "Mare Characterization", disabled: true },
];

const toolNavigation = [...reportNavigation, ...catalogueNavigation];

const accountNavigation = [
	{ to: "/membership", icon: "fa fa-star", text: "Membership & credits" },
	{ to: "/transactions", icon: "fa fa-receipt", text: "Transactions" },
];

function NavigationGroup({ label, items, onNavigate }) {
	return (
		<div className="sidebar__group">
			<p className="sidebar__group-label">{label}</p>
			{items.map((item) => item.disabled ? (
				<button key={item.text} type="button" className="sidebar__action sidebar__action--coming-soon" disabled>
					<SidebarItem {...item} />
					<span className="sidebar__coming-soon">Coming soon!</span>
				</button>
			) : (
				<NavLink
					key={item.to}
					to={item.to}
					end={item.end}
					className={`sidebar__link${item.featured ? " sidebar__link--featured" : ""}`}
					onClick={onNavigate}
					data-tour-id={item.tourId}
				>
					{({ isActive }) => <SidebarItem {...item} active={isActive} />}
				</NavLink>
			))}
		</div>
	);
}

function Sidebar({ isMobileOpen = false, onClose = () => {} }) {
	const currentUser = authenticationService.currentUserValue;
	const [isAddHorseModalOpen, setIsAddHorseModalOpen] = useState(false);
	const [alertProps, showAlert] = useSourAlert();

	const handleAddHorseResult = (result) => {
		if (result.type === "success") {
			showAlert(addHorseAlerts.success);
		} else {
			showAlert(addHorseAlerts.error(result.message));
		}
	};

	const openAddHorseModal = () => {
		setIsAddHorseModalOpen(true);
		onClose();
	};

	return (
		<>
			<button
				type="button"
				className={`sidebar-backdrop${isMobileOpen ? " sidebar-backdrop--visible" : ""}`}
				onClick={onClose}
				aria-label="Close navigation"
				tabIndex={isMobileOpen ? 0 : -1}
			/>
			<aside
				id="primary-navigation"
				className={`sidebar${isMobileOpen ? " sidebar--open" : ""}`}
				aria-label="Primary navigation"
			>
				<div className="sidebar__mobile-header">
					<span>Menu</span>
					<button type="button" onClick={onClose} aria-label="Close navigation">
						<i className="fa fa-times" aria-hidden="true" />
					</button>
				</div>
				<nav className="sidebar__navigation">
					<NavigationGroup label="Home" items={primaryNavigation} onNavigate={onClose} />
					<NavigationGroup label="Tools" items={toolNavigation} onNavigate={onClose} />
					<NavigationGroup label="Account" items={accountNavigation} onNavigate={onClose} />
					{currentUser?.user?.is_staff ? (
						<NavigationGroup
							label="Administration"
							items={[
								{
									to: "/functionalities-for-admin",
									icon: "fa fa-cog",
									text: "Admin tools",
								},
							]}
							onNavigate={onClose}
						/>
					) : null}
				</nav>
				<div className="sidebar__footer">
					<button type="button" className="sidebar__action" onClick={openAddHorseModal}>
						<SidebarItem
							icon="sidebar-icon-composite"
							text="Add a new horse"
							isCompositeIcon
						/>
					</button>
				</div>
			</aside>
			<AddHorseModal
				isOpen={isAddHorseModalOpen}
				onClose={() => setIsAddHorseModalOpen(false)}
				onResult={handleAddHorseResult}
			/>
			<SourAlert {...alertProps} />
		</>
	);
}

export default Sidebar;
